import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Plus, Trash, CheckCircle, XCircle, PlayCircle, BookOpenText, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY_LINE = { account_code: "", debit: "", credit: "", description: "" };

function ProposeDialog({ open, onOpenChange, year, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ description: "", reason: "", auditor_name: "", audit_date: "", audit_reference: "", evidence_note: "" });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ description: "", reason: "", auditor_name: "", audit_date: "", audit_reference: "", evidence_note: "" });
    setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
    api.get("/finance/gl/accounts").then(({ data }) => setAccounts((data.items || []).filter((a) => !a.is_group))).catch(() => {});
  }, [open]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const totals = useMemo(() => {
    const dr = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const cr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    return { dr: Math.round(dr * 100) / 100, cr: Math.round(cr * 100) / 100 };
  }, [lines]);
  const balanced = Math.abs(totals.dr - totals.cr) < 0.005 && totals.dr > 0;

  const save = async () => {
    if (!form.description.trim() || !form.reason.trim() || !form.auditor_name.trim()) {
      toast.error("Description, reason and auditor name are required"); return;
    }
    setBusy(true);
    try {
      const payload = {
        fiscal_year: year, ...form,
        lines: lines.filter((l) => l.account_code).map((l) => ({
          account_code: l.account_code, debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0, description: l.description || "",
        })),
      };
      const { data } = await api.post("/finance/gl/audit-adjustments", payload);
      toast.success(`${data.ref} proposed — awaiting approval by a second user`);
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="aadj-propose-dialog">
        <DialogHeader>
          <DialogTitle>Propose audit adjustment · FY {year}</DialogTitle>
          <DialogDescription>Maker-checker: a different user must approve, and then post, this adjustment.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Depreciation understatement per audit finding 4.2" data-testid="aadj-description" />
          </div>
          <div className="col-span-2">
            <Label>Reason</Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="aadj-reason" />
          </div>
          <div>
            <Label>Auditor name</Label>
            <Input value={form.auditor_name} onChange={(e) => setForm({ ...form, auditor_name: e.target.value })} data-testid="aadj-auditor" />
          </div>
          <div>
            <Label>Audit date</Label>
            <Input type="date" value={form.audit_date} onChange={(e) => setForm({ ...form, audit_date: e.target.value })} />
          </div>
          <div>
            <Label>Audit reference</Label>
            <Input value={form.audit_reference} onChange={(e) => setForm({ ...form, audit_reference: e.target.value })} placeholder="Mgmt letter §4.2" />
          </div>
          <div>
            <Label>Evidence note</Label>
            <Input value={form.evidence_note} onChange={(e) => setForm({ ...form, evidence_note: e.target.value })} placeholder="Signed audit schedule on file" />
          </div>
        </div>
        <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_110px_1fr_32px] items-center gap-2">
              <Select value={l.account_code || undefined} onValueChange={(v) => setLine(i, "account_code", v)}>
                <SelectTrigger className="h-9" data-testid={`aadj-line-account-${i}`}><SelectValue placeholder="Account…" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" min="0" step="0.01" placeholder="Debit" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} data-testid={`aadj-line-debit-${i}`} />
              <Input type="number" min="0" step="0.01" placeholder="Credit" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} data-testid={`aadj-line-credit-${i}`} />
              <Input placeholder="Line description" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} />
              <Button variant="outline" size="sm" className="h-9 px-2" disabled={lines.length <= 2}
                onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}>
                <Trash size={12} />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, { ...EMPTY_LINE }])} data-testid="aadj-add-line">
            <Plus size={12} className="mr-1" /> Add line
          </Button>
          <div className="text-sm font-mono-alt tabular-nums" data-testid="aadj-balance-indicator">
            DR <b>{formatCurrency(totals.dr)}</b> · CR <b>{formatCurrency(totals.cr)}</b> ·{" "}
            <span className={balanced ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>
              {balanced ? "Balanced ✓" : `Off by ${formatCurrency(Math.abs(totals.dr - totals.cr))}`}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-primary-blue" onClick={save} disabled={busy || !balanced} data-testid="aadj-save-btn">{busy ? "Saving…" : "Propose adjustment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_TONE = {
  proposed: "bg-amber-50 text-amber-700",
  approved: "bg-blue-50 text-blue-700",
  posted: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
};

export default function AuditAdjustmentsSection({ canEdit }) {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [items, setItems] = useState([]);
  const [impact, setImpact] = useState(null);
  const [showImpact, setShowImpact] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: list }, { data: imp }] = await Promise.all([
        api.get("/finance/gl/audit-adjustments", { params: { fiscal_year: year } }),
        api.get("/finance/gl/audit-adjustments/impact", { params: { fiscal_year: year } }),
      ]);
      setItems(list.items || []);
      setImpact(imp);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const act = async (id, action, body) => {
    try {
      const { data } = await api.post(`/finance/gl/audit-adjustments/${id}/${action}`, body || {});
      toast.success(action === "post" ? `Posted as ${data.journal_entry_no}` : `Adjustment ${data.status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Rejection reason:");
    if (!reason) return;
    await act(id, "reject", { reason });
  };

  return (
    <div className="dc-card overflow-hidden" data-testid="audit-adjustments-section">
      <div className="border-b border-slate-100 px-5 py-3 flex flex-wrap items-center gap-2">
        <BookOpenText size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Audit Adjustments</h3>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">Propose → approve → post (maker-checker)</span>
        <div className="ml-auto flex items-center gap-2">
          <Label className="text-xs text-slate-500">FY</Label>
          <Input type="number" className="h-8 w-24" value={year} onChange={(e) => setYear(parseInt(e.target.value) || year)} data-testid="aadj-year-input" />
          {canEdit && (
            <Button size="sm" className="btn-primary-blue h-8" onClick={() => setProposeOpen(true)} data-testid="aadj-propose-btn">
              <Plus size={12} className="mr-1" /> Propose
            </Button>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-6 text-sm text-slate-500" data-testid="aadj-empty">No audit adjustments for FY {year}. Propose one to record auditor-required corrections against the year-end ledger.</div>
      ) : (
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[960px]" data-testid="aadj-table">
            <thead><tr><th>Ref</th><th>Description</th><th>Auditor</th><th className="text-right">Amount</th><th>Status</th><th>Journal</th><th className="w-40">Actions</th></tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`aadj-row-${a.id}`}>
                  <td className="px-5 py-3 font-mono-alt text-xs font-semibold">{a.ref}</td>
                  <td className="text-sm max-w-[240px] truncate" title={a.description}>{a.description}</td>
                  <td className="text-xs text-slate-600">{a.auditor_name}{a.audit_reference ? ` · ${a.audit_reference}` : ""}</td>
                  <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(a.total_debit)}</td>
                  <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[a.status] || "bg-slate-100 text-slate-600"}`} data-testid={`aadj-status-${a.id}`}>{a.status.toUpperCase()}</span></td>
                  <td className="font-mono-alt text-xs">{a.journal_entry_no || "—"}</td>
                  <td>
                    {canEdit && (
                      <div className="flex gap-1">
                        {a.status === "proposed" && (
                          <>
                            <Button variant="outline" size="sm" className="h-7" onClick={() => act(a.id, "approve")} data-testid={`aadj-approve-${a.id}`}>
                              <CheckCircle size={12} className="mr-1" /> Approve
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => reject(a.id)} data-testid={`aadj-reject-${a.id}`}><XCircle size={12} /></Button>
                          </>
                        )}
                        {a.status === "approved" && (
                          <Button size="sm" className="btn-primary-blue h-7" onClick={() => act(a.id, "post")} data-testid={`aadj-post-${a.id}`}>
                            <PlayCircle size={12} className="mr-1" /> Post journal
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {impact && impact.posted_adjustments > 0 && (
        <div className="border-t border-slate-100 px-5 py-3">
          <button type="button" className="flex items-center gap-1.5 text-xs font-semibold text-slate-700" onClick={() => setShowImpact((v) => !v)} data-testid="aadj-impact-toggle">
            {showImpact ? <CaretUp size={12} /> : <CaretDown size={12} />}
            Original vs Adjusted impact · {impact.posted_adjustments} posted adjustment(s)
          </button>
          {showImpact && (
            <table className="data-table w-full mt-2" data-testid="aadj-impact-table">
              <thead><tr><th>Account</th><th className="text-right">Original net</th><th className="text-right">Adjustment</th><th className="text-right">Adjusted net</th></tr></thead>
              <tbody>
                {impact.accounts.map((r) => (
                  <tr key={r.account_code}>
                    <td className="font-mono-alt text-xs">{r.account_code} · {r.account_name}</td>
                    <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(r.original_net)}</td>
                    <td className={`text-right font-mono-alt text-xs tabular-nums font-semibold ${r.adjustment_net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{r.adjustment_net >= 0 ? "+" : ""}{formatCurrency(r.adjustment_net)}</td>
                    <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(r.adjusted_net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <ProposeDialog open={proposeOpen} onOpenChange={setProposeOpen} year={year} onSaved={load} />
    </div>
  );
}
